const base = import.meta.env.DEV ? '' : ''

export function getStoredToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token')
}

export function setStoredToken(t, rememberMe) {
  if (rememberMe) {
    localStorage.setItem('token', t)
    sessionStorage.removeItem('token')
  } else {
    sessionStorage.setItem('token', t)
    localStorage.removeItem('token')
  }
}

export function clearStoredToken() {
  localStorage.removeItem('token')
  sessionStorage.removeItem('token')
}

const token = () => getStoredToken()

export const api = {
  async request(method, path, options = {}) {
    const url = `${base}/api${path}`
    const headers = {
      ...options.headers,
    }
    if (token()) headers['Authorization'] = `Bearer ${token()}`
    let res
    try {
      res = await fetch(url, {
        method,
        ...options,
        headers,
      })
    } catch (e) {
      if (e?.name === 'AbortError') throw new Error('Request aborted')
      throw e
    }
    if (res.status === 401) {
      const hadToken = !!token()
      if (hadToken) {
        clearStoredToken()
        window.location.href = '/login'
        throw new Error('Nicht autorisiert')
      }
      let detail = 'E-Mail oder Passwort falsch.'
      try {
        const text = await res.text()
        const json = text ? JSON.parse(text) : {}
        detail = json.detail || detail
      } catch (_) {}
      throw new Error(detail)
    }
    if (res.status === 402) {
      let detail = 'Insufficient credits.'
      try {
        const text = await res.text()
        const json = text ? JSON.parse(text) : {}
        detail = json.detail || detail
      } catch (_) {}
      throw new Error(detail)
    }
    if (!res.ok) {
      if (res.status === 404 && (method === 'DELETE' || method === 'PATCH') && (path.includes('/jobs/') || path.includes('/tracks/'))) {
        try { await res.text() } catch (e) { if (e?.name !== 'AbortError') throw e }
        return null
      }
      let text
      try {
        text = await res.text()
      } catch (e) {
        if (e?.name === 'AbortError') throw new Error('Request aborted')
        throw e
      }
      let err
      try {
        err = JSON.parse(text)
      } catch {
        err = { detail: text }
      }
      throw new Error(err.detail || err.message || String(res.status))
    }
    if (res.status === 204 || res.headers.get('content-length') === '0') return null
    const contentType = res.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      let text
      try {
        text = await res.text()
      } catch (e) {
        if (e?.name === 'AbortError') throw new Error('Request aborted')
        throw e
      }
      const body = text ? JSON.parse(text) : null
      // Für GET: Body + Header zurückgeben (z. B. X-Total-Count für /tracks)
      if (method === 'GET') return { _body: body, _headers: res.headers }
      return body
    }
    return res
  },
  get(path) {
    return this.request('GET', path).then((result) => {
      if (result && typeof result._body !== 'undefined') return { data: result._body, headers: result._headers }
      return { data: result }
    })
  },
  post(path, body, opts = {}) {
    const isForm = body instanceof FormData
    const isURLParams = body instanceof URLSearchParams
    const reqBody = isForm ? body : (isURLParams ? body.toString() : JSON.stringify(body))
    const contentType = isForm ? {} : { 'Content-Type': isURLParams ? 'application/x-www-form-urlencoded' : 'application/json' }
    return this.request('POST', path, {
      ...opts,
      body: reqBody,
      headers: {
        ...contentType,
        ...opts.headers,
      },
    }).then((data) => ({ data }))
  },

  /**
   * POST FormData with live upload progress (XMLHttpRequest). onProgress(0–100) called during upload.
   */
  uploadWithProgress(path, formData, onProgress) {
    const baseUrl = base || ''
    const url = `${baseUrl}/api${path}`
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && typeof onProgress === 'function') {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(Math.min(99, percent))
        }
      })
      xhr.addEventListener('load', () => {
        if (xhr.status === 401) {
          clearStoredToken()
          window.location.href = '/login'
          reject(new Error('Nicht autorisiert'))
          return
        }
        if (xhr.status === 402) {
          try {
            const j = JSON.parse(xhr.responseText || '{}')
            reject(new Error(j.detail || 'Insufficient credits.'))
          } catch {
            reject(new Error('Insufficient credits.'))
          }
          return
        }
        const ok = xhr.status >= 200 && xhr.status < 300
        if (!ok) {
          let detail = xhr.statusText
          try {
            const j = JSON.parse(xhr.responseText || '{}')
            detail = j.detail || j.message || detail
          } catch (_) {}
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[Upload] API error', { status: xhr.status, statusText: xhr.statusText, body: xhr.responseText?.slice(0, 500) })
          }
          reject(new Error(detail))
          return
        }
        if (typeof onProgress === 'function') onProgress(100)
        try {
          const data = xhr.responseText ? JSON.parse(xhr.responseText) : null
          resolve({ data: data ?? { uploaded: 1, tracks: [] } })
        } catch (_) {
          // 200 OK but body not valid JSON → treat as success (e.g. proxy truncated response)
          resolve({ data: { uploaded: 1, tracks: [] } })
        }
      })
      xhr.addEventListener('error', () => reject(new Error('Upload fehlgeschlagen')))
      xhr.addEventListener('abort', () => reject(new Error('Request aborted')))
      xhr.open('POST', url)
      const t = token()
      if (t) xhr.setRequestHeader('Authorization', `Bearer ${t}`)
      xhr.send(formData)
    })
  },
  put(path, body, opts = {}) {
    return this.request('PUT', path, {
      ...opts,
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...opts.headers },
    }).then((data) => ({ data }))
  },
  patch(path, body, opts = {}) {
    return this.request('PATCH', path, {
      ...opts,
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...opts.headers },
    }).then((data) => ({ data }))
  },
  delete(path) {
    return this.request('DELETE', path).then(() => ({ data: null }))
  },
  async download(path, suggestedFilename) {
    const url = `${base}/api${path}`
    const headers = {}
    if (token()) headers['Authorization'] = `Bearer ${token()}`
    const res = await fetch(url, { method: 'GET', headers })
    if (res.status === 401) {
      clearStoredToken()
      window.location.href = '/login'
      throw new Error('Unauthorized')
    }
    if (!res.ok) throw new Error(res.statusText || 'Download fehlgeschlagen')
    let blob
    try {
      blob = await res.blob()
    } catch (e) {
      if (e?.name === 'AbortError') throw new Error('Request aborted')
      throw e
    }
    const u = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = u
    a.download = suggestedFilename || 'download'
    a.click()
    URL.revokeObjectURL(u)
  },
}

export function downloadUrl(trackId, format) {
  const t = token()
  const tok = t ? `&token=${encodeURIComponent(t)}` : ''
  return `${base}/api/download/${trackId}?format=${format}${tok}`
}

export function streamUrl(trackId, format) {
  return downloadUrl(trackId, format)
}

/** Waveform/Preview: MP3 bevorzugen (viel kleiner als WAV), sonst WAV/Original. */
export function previewUrl(trackId, { hasMp3, hasWav, hasOriginal } = {}) {
  if (hasMp3) return downloadUrl(trackId, 'mp3')
  if (hasWav) return downloadUrl(trackId, 'wav')
  if (hasOriginal) return downloadUrl(trackId, 'original')
  return downloadUrl(trackId, 'mp3')
}

/**
 * Massendownload: POST /api/download/bulk mit track_ids + format, streamt ZIP.
 * Nutzt showSaveFilePicker + stream wenn möglich (große Dateien), sonst Blob.
 */
export async function downloadBulkZip(trackIds, format = 'wav', suggestedName = 'masters_wav.zip') {
  if (!trackIds?.length) throw new Error('Keine Tracks ausgewählt')
  const url = `${base}/api/download/bulk`
  const headers = { 'Content-Type': 'application/json' }
  if (token()) headers['Authorization'] = `Bearer ${token()}`
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ track_ids: trackIds, format }),
  })
  if (res.status === 401) {
    clearStoredToken()
    window.location.href = '/login'
    throw new Error('Nicht autorisiert')
  }
  if (!res.ok) {
    const text = await res.text()
    let detail = res.statusText
    try {
      const j = JSON.parse(text)
      detail = j.detail || detail
    } catch (_) {}
    throw new Error(detail)
  }
  const useStream = typeof window !== 'undefined' && window.showSaveFilePicker != null
  if (useStream && res.body) {
    const fileHandle = await window.showSaveFilePicker({ suggestedName })
    const writable = await fileHandle.createWritable()
    await res.body.pipeTo(writable)
    await writable.close()
    return
  }
  const blob = await res.blob()
  const u = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = u
  a.download = suggestedName
  a.click()
  URL.revokeObjectURL(u)
}
